import { useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";
import { MultiSelect } from "primereact/multiselect";
import { sowCurrenciesForJurisdiction } from "../../../utils/sowCurrencies";
import "../css/KycSectionSowSof.css";
import "../mobile/KycMobileClientSections.css";

import iconSourceFunds from "../../../images/SoF.json";
import iconSourceWealth from "../../../images/SoW.json";
import iconPaymentRoutes from "../../../images/transfer2-lottie.json";
import iconTurnover from "../../../images/turnover.json";
import iconTurnoverField from "../../../images/refinancing.svg";
import iconNetWorth from "../../../images/piggy-bank.svg";
import iconAnnualIncome from "../../../images/managementX.svg";

// ─── Shared option lists ────────────────────────────────────────────────────

const SOW_OPTIONS = [
  { label: "Employment", value: "employment" },
  { label: "Self-employed", value: "self_employed" },
  { label: "Business owner", value: "business_owner" },
  { label: "Sale of company", value: "sale_business" },
  { label: "Real estate sale", value: "sale_real_estate" },
  { label: "Rental income", value: "rental_income" },
  { label: "Investment returns", value: "investment_returns" },
  { label: "Inheritance", value: "inheritance" },
  { label: "Gift", value: "gift" },
  { label: "Legal settlement", value: "legal_settlement" },
  { label: "Divorce settlement", value: "divorce_settlement" },
  { label: "Virtual asset gains", value: "virtual_asset_gains" },
  { label: "Other", value: "other" },
];

const SOF_ALL_OPTIONS = [
  { label: "Employment income", value: "employment_income", sow: ["employment"] },
  { label: "Professional income", value: "professional_income", sow: ["self_employed"] },
  { label: "Business income", value: "business_income", sow: ["business_owner", "self_employed"] },
  { label: "Sale of business proceeds", value: "sale_business_proceeds", sow: ["sale_business", "business_owner"] },
  { label: "Sale of property proceeds", value: "sale_property_proceeds", sow: ["sale_real_estate"] },
  { label: "Rental income", value: "rental_income_sof", sow: ["rental_income"] },
  { label: "Sale of securities", value: "sale_securities", sow: ["investment_returns"] },
  { label: "Dividends distribution", value: "dividends_distribution", sow: ["business_owner", "investment_returns"] },
  { label: "Inheritance proceeds", value: "inheritance_proceeds", sow: ["inheritance"] },
  { label: "Gift proceeds", value: "gift_proceeds", sow: ["gift"] },
  { label: "Legal settlement proceeds", value: "legal_settlement_proceeds", sow: ["legal_settlement"] },
  { label: "Divorce settlement proceeds", value: "divorce_settlement_proceeds", sow: ["divorce_settlement"] },
  { label: "Sale of virtual assets", value: "sale_virtual_assets", sow: ["virtual_asset_gains"] },
  { label: "Loan", value: "loan", sow: null },
  { label: "Other", value: "other", sow: null },
];

const PAYMENT_ROUTES_OPTIONS = [
  { label: "Transfer from own account", value: "transfer_own_account" },
  { label: "Third-party payment", value: "third_party_payment" },
  { label: "Cash deposit", value: "cash_deposit" },
  { label: "Cheque", value: "cheque" },
  { label: "Card payment", value: "card_payment" },
  { label: "Virtual assets transfer", value: "virtual_assets_transfer" },
  { label: "Other", value: "other" },
];

const NET_WORTH_CURRENCIES = ["USD", "AED"];

const NET_WORTH_BUCKET_KEYS = [
  "<50k", "50k-100k", "100k-250k", "250k-500k", "500k-1m",
  "1m-2m", "2m-5m", "5m-10m", "10m-20m", "20m-50m", "50m-100m", "100m+",
];

const NET_WORTH_LABELS = {
  USD: {
    "<50k": "< 50k", "50k-100k": "50k – 100k", "100k-250k": "100k – 250k",
    "250k-500k": "250k – 500k", "500k-1m": "500k – 1m", "1m-2m": "1m – 2m",
    "2m-5m": "2m – 5m", "5m-10m": "5m – 10m", "10m-20m": "10m – 20m",
    "20m-50m": "20m – 50m", "50m-100m": "50m – 100m", "100m+": "100m+",
  },
  AED: {
    "<50k": "< 200k", "50k-100k": "200k – 400k", "100k-250k": "400k – 1m",
    "250k-500k": "1m – 2m", "500k-1m": "2m – 4m", "1m-2m": "4m – 8m",
    "2m-5m": "8m – 20m", "5m-10m": "20m – 40m", "10m-20m": "40m – 80m",
    "20m-50m": "80m – 200m", "50m-100m": "200m – 400m", "100m+": "400m+",
  },
};

const ANNUAL_INCOME_BUCKET_KEYS = [
  "<50k", "50k-100k", "100k-150k", "150k-250k", "250k-350k", "350k-500k",
  "500k-750k", "750k-1m", "1m-2m", "2m-5m", "5m-10m", "10m+",
];

const ANNUAL_INCOME_LABELS = {
  USD: {
    "<50k": "< 50k", "50k-100k": "50k – 100k", "100k-150k": "100k – 150k",
    "150k-250k": "150k – 250k", "250k-350k": "250k – 350k", "350k-500k": "350k – 500k",
    "500k-750k": "500k – 750k", "750k-1m": "750k – 1m", "1m-2m": "1m – 2m",
    "2m-5m": "2m – 5m", "5m-10m": "5m – 10m", "10m+": "10m+",
  },
  AED: {
    "<50k": "< 200k", "50k-100k": "200k – 400k", "100k-150k": "400k – 550k",
    "150k-250k": "550k – 1m", "250k-350k": "1m – 1.3m", "350k-500k": "1.3m – 2m",
    "500k-750k": "2m – 2.8m", "750k-1m": "2.8m – 4m", "1m-2m": "4m – 8m",
    "2m-5m": "8m – 20m", "5m-10m": "20m – 40m", "10m+": "40m+",
  },
};

const ANNUAL_TURNOVER_BUCKET_KEYS = [
  "<100k", "100k-250k", "250k-500k", "500k-1m", "1m-2m", "2m-5m",
  "5m-10m", "10m-25m", "25m-50m", "50m-100m", "100m+",
];

const ANNUAL_TURNOVER_LABELS = {
  USD: {
    "<100k": "< 100k", "100k-250k": "100k – 250k", "250k-500k": "250k – 500k",
    "500k-1m": "500k – 1m", "1m-2m": "1m – 2m", "2m-5m": "2m – 5m",
    "5m-10m": "5m – 10m", "10m-25m": "10m – 25m", "25m-50m": "25m – 50m",
    "50m-100m": "50m – 100m", "100m+": "100m+",
  },
  AED: {
    "<100k": "< 400k", "100k-250k": "400k – 1m", "250k-500k": "1m – 2m",
    "500k-1m": "2m – 4m", "1m-2m": "4m – 8m", "2m-5m": "8m – 20m",
    "5m-10m": "20m – 40m", "10m-25m": "40m – 100m", "25m-50m": "100m – 200m",
    "50m-100m": "200m – 400m", "100m+": "400m+",
  },
};

// CHF uses the same numerical bands as USD, expressed in CHF (no FX conversion).
for (const labels of [NET_WORTH_LABELS, ANNUAL_INCOME_LABELS, ANNUAL_TURNOVER_LABELS]) {
  labels.CHF = labels.USD;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function toggleInArray(arr, value) {
  const set = new Set(arr || []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return Array.from(set);
}

function autoResizeTextarea(el) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * 3 + verticalExtras;
  const maxHeight = lineHeight * 10 + verticalExtras;
  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  el.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

const DETAIL_TEXTAREA_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";

function InlineDangerMessage({ children, centered = false }) {
  return (
    <div className={`kycInlineFieldError ${centered ? "kycInlineFieldError--centered" : ""}`}>
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

// ─── Old version preserved for rollback ──────────────────────────────────────

export function KycSectionSourceOfFundsWealth_old_version({
  sectionNo = 0,
  form,
  onSetAt,
  mode = "staff",
  changedClass,
  readOnly = false,
  copyOverrides = {},
  submitAttempted = false,
}) {
  const sow = form?.sourceOfFundsWealth || {};

  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const COPY = {
    client: {
      header: {
        kicker: "Section 3",
        title: " | Funding & Origin of Wealth",
        hint: "Please explain how the wealth was built (source of wealth) and more specifically where the funds come from (source of funds)",
      },
      fields: {
        sofLabel: "Source of funds (where the money comes from)",
        sowLabel: "Source of wealth (how the owners built their wealth)",
      },
    },
    staff: {
      header: { kicker: null, title: " | Source of funds & Source of wealth", hint: null },
      fields: { sofLabel: "Source of funds", sowLabel: "Source of wealth" },
    },
    review: {
      header: { kicker: null, title: " | Source of funds & Source of wealth", hint: null },
      fields: { sofLabel: "Source of funds", sowLabel: "Source of wealth" },
    },
  };

  const baseCopy = COPY[mode] || COPY.staff;
  const copy = {
    ...baseCopy, ...copyOverrides,
    header: { ...baseCopy.header, ...(copyOverrides.header || {}) },
    fields: { ...baseCopy.fields, ...(copyOverrides.fields || {}) },
  };

  const selectedSowTypes = sow.sourceOfWealth || [];
  const sofTypes = Array.isArray(sow.sourceOfFundsTypes) ? sow.sourceOfFundsTypes : [];
  const needsThirdPartyExplain = sofTypes.includes("third_party_non_family");
  const needsOtherExplain = sofTypes.includes("other");
  const showCashRiskHint = sofTypes.includes("cash_deposit");
  const showCryptoHint = sofTypes.includes("crypto_proceeds");
  const missingSow = submitAttempted && selectedSowTypes.length === 0;
  const missingNetWorth = submitAttempted && !String(sow.netWorthRange || "").trim();
  const missingAnnualIncome = submitAttempted && !String(sow.annualIncomeRange || "").trim();
  const missingSof = submitAttempted && sofTypes.length === 0;

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        {copy.header.kicker ? (
          <div className="kycSectionKicker">{copy.header.kicker}</div>
        ) : null}
        <h5 className="kycSectionTitle">
          {sectionNo}{copy.header.title}
        </h5>
        {copy.header.hint ? (
          <div className="kycSectionHint">{copy.header.hint}</div>
        ) : null}
      </div>

      <div className="kycSectionBody">
        <div className="kycSubSection kycSubCard">
          <div className="kycSubSectionTitle">Overall financial profile</div>
          <div className="kycSubSectionHint">
            Overview of how wealth was built and general financial capacity
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12">
              <div className="text-muted small mb-2">{copy.fields.sowLabel}</div>
              <div className={"row g-2 " + (cc(["sourceOfFundsWealth", "sourceOfWealth"]) ? "kycChangedWrap" : "")}>
                {SOW_OPTIONS.map((opt) => (
                  <div key={opt.value} className="col-3" style={{ paddingLeft: 50 }}>
                    <label className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        disabled={readOnly}
                        checked={(sow.sourceOfWealth || []).includes(opt.value)}
                        onChange={() =>
                          onSetAt(["sourceOfFundsWealth", "sourceOfWealth"],
                            toggleInArray(sow.sourceOfWealth, opt.value))
                        }
                      />
                      <span className="form-check-label sowCheckLabel">{opt.label}</span>
                    </label>
                  </div>
                ))}
              </div>
              {missingSow ? (
                <InlineDangerMessage centered>Select at least one source of wealth</InlineDangerMessage>
              ) : null}
              {(sow.sourceOfWealth || []).includes("other") && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Please specify</div>
                  <input className="form-control" value={sow.sourceOfWealthDetails || ""}
                    disabled={readOnly}
                    onChange={(e) => onSetAt(["sourceOfFundsWealth", "sourceOfWealthDetails"], e.target.value)}
                    placeholder="Describe briefly..."
                  />
                </div>
              )}
            </div>

            <div style={{ height: 5 }} />

            <div className="col-12 col-lg-6">
              <div className="text-muted small mb-1">Net worth (approx.)</div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <select className="form-select" style={{ maxWidth: 140 }}
                  value={sow.netWorthCurrency || "USD"} disabled={readOnly}
                  onChange={(e) => onSetAt(["sourceOfFundsWealth", "netWorthCurrency"], e.target.value)}>
                  {NET_WORTH_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className={`form-select kycSowRangeSelect ${sow.netWorthRange ? "is-selected" : ""}`}
                  style={{ width: "148px", maxWidth: "100%" }}
                  value={sow.netWorthRange || ""} disabled={readOnly}
                  onChange={(e) => onSetAt(["sourceOfFundsWealth", "netWorthRange"], e.target.value)}>
                  <option value="">Select</option>
                  {NET_WORTH_BUCKET_KEYS.map((key) => {
                    const cur = sow.netWorthCurrency || "USD";
                    return <option key={key} value={key}>{NET_WORTH_LABELS[cur]?.[key] || NET_WORTH_LABELS.USD[key]}</option>;
                  })}
                </select>
                {sow.netWorthCurrency === "AED" && sow.netWorthRange ? (
                  <span className="text-muted small">≈ {NET_WORTH_LABELS.USD[sow.netWorthRange]} USD</span>
                ) : null}
              </div>
              {missingNetWorth ? <InlineDangerMessage>Select a net worth range</InlineDangerMessage> : null}
            </div>

            <div className="col-12 col-lg-6">
              <div className="text-muted small mb-1">Annual income (approx.)</div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <select className="form-select" style={{ maxWidth: 140 }}
                  value={sow.annualIncomeCurrency || "USD"} disabled={readOnly}
                  onChange={(e) => onSetAt(["sourceOfFundsWealth", "annualIncomeCurrency"], e.target.value)}>
                  {NET_WORTH_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className={`form-select kycSowRangeSelect ${sow.annualIncomeRange ? "is-selected" : ""}`}
                  style={{ width: "148px", maxWidth: "100%" }}
                  value={sow.annualIncomeRange || ""} disabled={readOnly}
                  onChange={(e) => onSetAt(["sourceOfFundsWealth", "annualIncomeRange"], e.target.value)}>
                  <option value="">Select</option>
                  {ANNUAL_INCOME_BUCKET_KEYS.map((key) => {
                    const cur = sow.annualIncomeCurrency || "USD";
                    return <option key={key} value={key}>{ANNUAL_INCOME_LABELS[cur]?.[key] || ANNUAL_INCOME_LABELS.USD[key]}</option>;
                  })}
                </select>
                {sow.annualIncomeCurrency === "AED" && sow.annualIncomeRange ? (
                  <span className="text-muted small">≈ {ANNUAL_INCOME_LABELS.USD[sow.annualIncomeRange]} USD</span>
                ) : null}
              </div>
              {missingAnnualIncome ? <InlineDangerMessage>Select an annual income range</InlineDangerMessage> : null}
            </div>
          </div>
        </div>

        <div className="kycSubSection kycSubCard mt-4">
          <div className="kycSubSectionTitle">Funding source</div>
          <div className="kycSubSectionHint">
            Origin of funds entering the business relationship (immediate source of incoming funds)
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12">
              <div className="text-muted small mb-1">{copy.fields.sofLabel}</div>
              <div className={cc(["sourceOfFundsWealth", "sourceOfFundsTypes"]) ? "kycChangedWrap" : ""}>
                <MultiSelect
                  className="w-100 kycSofMultiSelect"
                  panelClassName="kycSofPanel"
                  value={sofTypes}
                  options={SOF_OPTIONS}
                  onChange={(e) => onSetAt(["sourceOfFundsWealth", "sourceOfFundsTypes"], e.value || [])}
                  disabled={readOnly}
                  placeholder="Select one or more…"
                  display="chip"
                  showSelectAll={false}
                  filter={false}
                  panelHeaderTemplate={() => null}
                  appendTo={document.body}
                  style={{ width: "100%" }}
                  panelStyle={{ width: 560, maxWidth: "calc(100vw - 24px)" }}
                />
              </div>
              {missingSof ? <InlineDangerMessage centered>Select at least one source of funds</InlineDangerMessage> : null}
              {(showCashRiskHint || showCryptoHint) && (
                <div className="text-muted small mt-2">
                  {showCashRiskHint ? "Note: cash deposits can require additional clarification." : null}
                  {showCashRiskHint && showCryptoHint ? " " : null}
                  {showCryptoHint ? "Note: crypto proceeds may require additional documentation." : null}
                </div>
              )}
              {needsThirdPartyExplain && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Please explain the third-party payment (non-family)</div>
                  <input className="form-control" value={sow.thirdPartyDetails || ""}
                    disabled={readOnly}
                    onChange={(e) => onSetAt(["sourceOfFundsWealth", "thirdPartyDetails"], e.target.value)}
                    placeholder="Who is paying, relationship, reason, and expected amount…"
                  />
                </div>
              )}
              {needsOtherExplain && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Please specify</div>
                  <input className="form-control" value={sow.sourceOfFundsOtherDetails || ""}
                    disabled={readOnly}
                    onChange={(e) => onSetAt(["sourceOfFundsWealth", "sourceOfFundsOtherDetails"], e.target.value)}
                    placeholder="Describe briefly…"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Current shared design ───────────────────────────────────────────────────

export default function KycSectionSourceOfFundsWealth({
  sectionNo = 0,
  form,
  orgJurisdiction,
  onSetAt,
  mode = "staff", // "client" | "staff" | "review"
  changedClass,
  changedArrayValue,
  readOnly = false,
  copyOverrides = {},
  submitAttempted = false,
}) {
  // Refs for auto-resizing textareas
  const currencies = sowCurrenciesForJurisdiction(orgJurisdiction);
  const sowOtherRef = useRef(null);
  const sofLoanRef = useRef(null);
  const sofOtherRef = useRef(null);
  const paymentRoutesThirdPartyRef = useRef(null);
  const paymentRoutesOtherRef = useRef(null);
  const rangeBtnRefs = useRef({});
  const [openRangeDropdown, setOpenRangeDropdown] = useState(null);
  const [rangeDropdownPos, setRangeDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const sow = form?.sourceOfFundsWealth || {};
  const customerType = String(
    form?.customer?.type ||
      form?.customer?.clientType ||
      form?.customer?.customerType ||
      ""
  )
    .trim()
    .toLowerCase();
  const isEntityClient =
    customerType === "entity" ||
    customerType === "company" ||
    customerType === "corporate";
  const ownership = form?.ownership || {};
  const ownershipProfile = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const ownershipHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const ownershipMode = String(
    ownership?.mobilePreliminaryMode ||
      ownership?.assistantPreliminaryMode ||
      ownership?.mobileAssistantDraft?.preliminaryMode ||
      ownership?.assistantTree?.initialAHType ||
      ownership?.preliminaryMode ||
      ""
  )
    .trim()
    .toLowerCase();
  const showsCompanyTurnover =
    isEntityClient &&
    (ownershipProfile === "operating" ||
      (ownershipProfile === "holding" && ownershipHoldingKind === "active") ||
      ownership?.preliminaryNoneOfAbove === true ||
      ownershipMode === "noneofabove" ||
      ownershipMode === "holdingactive");
  const selectedSowTypes = sow.sourceOfWealth || [];
  const sofTypes = Array.isArray(sow.sourceOfFundsTypes) ? sow.sourceOfFundsTypes : [];
  const paymentRoutes = Array.isArray(sow.paymentRoutes) ? sow.paymentRoutes : [];

  // SoF options available given current SoW selection
  const availableSofOptions = SOF_ALL_OPTIONS.filter(
    (opt) => opt.sow === null || opt.sow.some((k) => selectedSowTypes.includes(k))
  );

  const needsSofLoanExplain = sofTypes.includes("loan");
  const needsSofOtherExplain = sofTypes.includes("other");
  const needsThirdPartyPaymentExplain = paymentRoutes.includes("third_party_payment");
  const needsPaymentRoutesOtherExplain = paymentRoutes.includes("other");

  const missingSow = submitAttempted && selectedSowTypes.length === 0;
  const missingNetWorth = submitAttempted && !String(sow.netWorthRange || "").trim();
  const missingAnnualIncome = submitAttempted && !String(sow.annualIncomeRange || "").trim();
  const missingAnnualTurnover =
    showsCompanyTurnover &&
    submitAttempted &&
    !String(sow.annualTurnoverRange || "").trim();
  const missingSof = submitAttempted && sofTypes.length === 0;
  const missingPaymentRoutes = submitAttempted && paymentRoutes.length === 0;

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(sowOtherRef.current);
      autoResizeTextarea(sofLoanRef.current);
      autoResizeTextarea(sofOtherRef.current);
      autoResizeTextarea(paymentRoutesThirdPartyRef.current);
      autoResizeTextarea(paymentRoutesOtherRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    sow.sourceOfWealthDetails,
    sow.sourceOfFundsLoanDetails,
    sow.sourceOfFundsOtherDetails,
    sow.paymentRoutesThirdPartyDetails,
    sow.paymentRoutesOtherDetails,
    sow.sourceOfWealth,
    sow.sourceOfFundsTypes,
    sow.paymentRoutes,
  ]);

  const COPY = {
    client: {
      header: {
        kicker: "Section 3",
        title: " | Funding & Origin of Wealth",
        hint: "Please explain how the wealth was built (source of wealth) and more specifically where the funds come from (source of funds)",
      },
      fields: {
        sofLabel: "Source of funds",
        sowLabel: "Source of wealth",
      },
    },
    staff: {
      header: {
        kicker: null,
        title: " | Source of funds & Source of wealth",
        hint: null,
      },
      fields: {
        sofLabel: "Source of funds",
        sowLabel: "Source of wealth",
      },
    },
    review: {
      header: {
        kicker: null,
        title: " | Source of funds & Source of wealth",
        hint: null,
      },
      fields: {
        sofLabel: "Source of funds",
        sowLabel: "Source of wealth",
      },
    },
  };

  const baseCopy = COPY[mode] || COPY.staff;
  const header = { ...baseCopy.header, ...(copyOverrides.header || {}) };
  const fields = { ...baseCopy.fields, ...(copyOverrides.fields || {}) };
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";
  const changedArrayItem = (path, value) =>
    typeof changedArrayValue === "function"
      ? changedArrayValue(path, value)
      : false;

  function openRangePicker(key) {
    if (readOnly) return;
    if (openRangeDropdown === key) {
      setOpenRangeDropdown(null);
      return;
    }
    const rect = rangeBtnRefs.current[key]?.getBoundingClientRect?.();
    if (rect) {
      setRangeDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpenRangeDropdown(key);
  }

  useLayoutEffect(() => {
    if (!openRangeDropdown) return undefined;

    const updateDropdownPosition = () => {
      const rect =
        rangeBtnRefs.current[openRangeDropdown]?.getBoundingClientRect?.();
      if (!rect) {
        setOpenRangeDropdown(null);
        return;
      }
      setRangeDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [openRangeDropdown]);

  function renderRangePicker({
    dropdownKey,
    value,
    currency,
    bucketKeys,
    labels,
    path,
    className,
  }) {
    const options = [
      { value: "", label: "Select" },
      ...bucketKeys.map((key) => ({
        value: key,
        label: labels[currency]?.[key] || labels.USD[key],
      })),
    ];
    const selectedLabel =
      options.find((opt) => String(opt.value) === String(value || ""))?.label ||
      "Select";

    return (
      <>
        <button
          ref={(el) => {
            rangeBtnRefs.current[dropdownKey] = el;
          }}
          type="button"
          className={className}
          disabled={readOnly}
          onClick={() => openRangePicker(dropdownKey)}
        >
          <span className="kycSowRangePickerText">{selectedLabel}</span>
        </button>
        {!readOnly && openRangeDropdown === dropdownKey
          ? createPortal(
              <>
                <div
                  className="kycSowRangeDismiss"
                  onClick={() => setOpenRangeDropdown(null)}
                />
                <div
                  className="kycSowRangeDropdown"
                  style={{
                    position: "fixed",
                    top: rangeDropdownPos.top,
                    left: rangeDropdownPos.left,
                    minWidth: rangeDropdownPos.width || 130,
                  }}
                >
                  {options.map((opt) => (
                    <button
                      key={opt.value || "__select__"}
                      type="button"
                      className={
                        "kycSowRangeOption" +
                        (String(value || "") === String(opt.value)
                          ? " is-active"
                          : "")
                      }
                      onClick={() => {
                        onSetAt(path, opt.value);
                        setOpenRangeDropdown(null);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )
          : null}
      </>
    );
  }

  function renderCurrencyPicker({ dropdownKey, value, path, className }) {
    const currentValue = value || "USD";

    return (
      <>
        <button
          ref={(el) => {
            rangeBtnRefs.current[dropdownKey] = el;
          }}
          type="button"
          className={className}
          disabled={readOnly}
          onClick={() => openRangePicker(dropdownKey)}
        >
          <span className="kycSowRangePickerText">{currentValue}</span>
        </button>
        {!readOnly && openRangeDropdown === dropdownKey
          ? createPortal(
              <>
                <div
                  className="kycSowRangeDismiss"
                  onClick={() => setOpenRangeDropdown(null)}
                />
                <div
                  className="kycSowRangeDropdown kycSowCurrencyDropdown"
                  style={{
                    position: "fixed",
                    top: rangeDropdownPos.top,
                    left: rangeDropdownPos.left,
                    minWidth: rangeDropdownPos.width || 72,
                  }}
                >
                  {currencies.map((currencyOption) => (
                    <button
                      key={currencyOption}
                      type="button"
                      className={
                        "kycSowRangeOption" +
                        (currentValue === currencyOption ? " is-active" : "")
                      }
                      onClick={() => {
                        onSetAt(path, currencyOption);
                        setOpenRangeDropdown(null);
                      }}
                    >
                      {currencyOption}
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )
          : null}
      </>
    );
  }

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        {header.kicker ? (
          <div className="kycSectionKicker">{header.kicker}</div>
        ) : null}
        <h5 className="kycSectionTitle">
          {sectionNo}{header.title}
        </h5>
        {header.hint ? (
          <div className="kycSectionHint">{header.hint}</div>
        ) : null}
      </div>

      <div className="kycSectionBody">
        <div className="kycMobileSectionStack">
          {showsCompanyTurnover ? (
            <>
              <div className="kycMobileRepeaterCard" style={{ background: "#F6F4F2" }}>
                <div className="kycMobileRepeaterHead">
                  <div className="kycMobileSectionIconWrap">
                    <Lottie
                      animationData={iconTurnover}
                      loop
                      autoplay
                      className="kycMobileSectionLottie"
                    />
                  </div>
                  <div>
                    <div className="kycMobileRepeaterTitle">Company turnover</div>
                    <div className="kycMobileRepeaterHint">
                      Annual turnover of the company
                    </div>
                  </div>
                </div>

                <div className="kycMobileSectionStack">
                  <div className="kycMobileFieldCard kycMobileCompoundFieldCard">
                    <div className="kycMobileCompoundFieldIconCol">
                      <img src={iconTurnoverField} alt="" className="kycMobileCompoundFieldIcon" />
                    </div>
                    <div className="kycMobileCompoundFieldBody">
                      <label className="kycMobileFieldLabel">Annual turnover</label>
                      <div className="kycMobileCompoundFieldInputs">
                        {renderCurrencyPicker({
                          dropdownKey: "annualTurnoverCurrency",
                          value: sow.annualTurnoverCurrency || "USD",
                          path: ["sourceOfFundsWealth", "annualTurnoverCurrency"],
                          className:
                            "form-select kycMobileCompactSelect kycMobileCompactSelect--currency",
                        })}
                        {renderRangePicker({
                          dropdownKey: "annualTurnoverRange",
                          value: sow.annualTurnoverRange || "",
                          currency: sow.annualTurnoverCurrency || "USD",
                          bucketKeys: ANNUAL_TURNOVER_BUCKET_KEYS,
                          labels: ANNUAL_TURNOVER_LABELS,
                          path: ["sourceOfFundsWealth", "annualTurnoverRange"],
                          className: `form-select kycMobileCompactSelect ${
                            sow.annualTurnoverRange ? "is-selected" : ""
                          } kycMobileCompactSelect--range kycMobileCompactSelect--turnoverRange ${
                            cc(["sourceOfFundsWealth", "annualTurnoverRange"])
                              ? "kycReviewChangedChip"
                              : ""
                          }`,
                        })}
                        {sow.annualTurnoverCurrency === "AED" && sow.annualTurnoverRange ? (
                          <span className="kycSowApproxLabel">
                            ≈ {ANNUAL_TURNOVER_LABELS.USD[sow.annualTurnoverRange]} USD
                          </span>
                        ) : null}
                      </div>
                      {missingAnnualTurnover ? (
                        <InlineDangerMessage>Select an annual turnover range</InlineDangerMessage>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {/* ── Card 1: Overall financial profile ── */}
          <div
            className="kycMobileRepeaterCard"
            style={{ background: "#F6F4F2", marginTop: showsCompanyTurnover ? 10 : 0 }}
          >
            <div className="kycMobileRepeaterHead">
              <div className="kycMobileSectionIconWrap">
                <Lottie
                  animationData={iconSourceWealth}
                  loop
                  autoplay
                  className="kycMobileSectionLottie"
                />
              </div>
              <div>
                <div className="kycMobileRepeaterTitle">Overall financial profile</div>
                <div className="kycMobileRepeaterHint">
                  Overview of how wealth was built and general financial capacity
                </div>
              </div>
            </div>

            <div className="kycMobileSectionStack">
              {/* Source of wealth chips */}
              <div className="kycMobileFieldCard">
                <label className="kycMobileFieldLabel">{fields.sowLabel}</label>
                <div className="kycMobileFieldHint">Select one or multiple sources</div>
                <div className="kycMobileChipGrid">
                  {SOW_OPTIONS.map((opt) => {
                    const active = selectedSowTypes.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`kycMobileChip ${active ? "is-active" : ""} ${
                          mode === "review" &&
                          active &&
                          changedArrayItem(
                            ["sourceOfFundsWealth", "sourceOfWealth"],
                            opt.value
                          )
                            ? "kycReviewChangedChip"
                            : ""
                        }`}
                        style={active ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                        disabled={readOnly}
                        onClick={() =>
                          onSetAt(
                            ["sourceOfFundsWealth", "sourceOfWealth"],
                            toggleInArray(sow.sourceOfWealth, opt.value)
                          )
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {missingSow ? (
                  <InlineDangerMessage centered>Select at least one source of wealth</InlineDangerMessage>
                ) : null}
              </div>

              <AnimatePresence>
                {selectedSowTypes.includes("other") ? (
                  <motion.div
                    key="sow-other"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: 0.22, delay: 0.1 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="kycMobileFieldCard">
                      <label className="kycMobileFieldLabel">Other source of wealth</label>
                      <textarea
                        ref={sowOtherRef}
                        className={`form-control kycMobileAutoGrowTextarea kycSowNarrativeTextarea ${cc([
                          "sourceOfFundsWealth",
                          "sourceOfWealthDetails",
                        ])}`}
                        rows={3}
                        style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                        value={sow.sourceOfWealthDetails || ""}
                        onChange={(e) => {
                          autoResizeTextarea(e.currentTarget);
                          onSetAt(["sourceOfFundsWealth", "sourceOfWealthDetails"], e.target.value);
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        disabled={readOnly}
                        placeholder="Describe briefly…"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              {/* Net worth + Annual income — side by side */}
              <div className="kycSowRangeRow" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {/* Net worth */}
                <div className="kycMobileFieldCard kycMobileCompoundFieldCard" style={{ flex: "1 1 240px" }}>
                  <div className="kycMobileCompoundFieldIconCol">
                    <img src={iconNetWorth} alt="" className="kycMobileCompoundFieldIcon" />
                  </div>
                  <div className="kycMobileCompoundFieldBody">
                    <label className="kycMobileFieldLabel">Net worth</label>
                    <div className="kycMobileCompoundFieldInputs">
                      {renderCurrencyPicker({
                        dropdownKey: "netWorthCurrency",
                        value: sow.netWorthCurrency || "USD",
                        path: ["sourceOfFundsWealth", "netWorthCurrency"],
                        className:
                          "form-select kycMobileCompactSelect kycMobileCompactSelect--currency",
                      })}
                      {renderRangePicker({
                        dropdownKey: "netWorthRange",
                        value: sow.netWorthRange || "",
                        currency: sow.netWorthCurrency || "USD",
                        bucketKeys: NET_WORTH_BUCKET_KEYS,
                        labels: NET_WORTH_LABELS,
                        path: ["sourceOfFundsWealth", "netWorthRange"],
                        className: `form-select kycMobileCompactSelect ${
                          sow.netWorthRange ? "is-selected" : ""
                        } kycMobileCompactSelect--range ${
                          cc(["sourceOfFundsWealth", "netWorthRange"])
                            ? "kycReviewChangedChip"
                            : ""
                        }`,
                      })}
                      {sow.netWorthCurrency === "AED" && sow.netWorthRange ? (
                        <span className="kycSowApproxLabel">
                          ≈ {NET_WORTH_LABELS.USD[sow.netWorthRange]} USD
                        </span>
                      ) : null}
                    </div>
                    {missingNetWorth ? (
                      <InlineDangerMessage>Select a net worth range</InlineDangerMessage>
                    ) : null}
                  </div>
                </div>

                {/* Annual income */}
                <div className="kycMobileFieldCard kycMobileCompoundFieldCard" style={{ flex: "1 1 240px" }}>
                  <div className="kycMobileCompoundFieldIconCol">
                    <img src={iconAnnualIncome} alt="" className="kycMobileCompoundFieldIcon" />
                  </div>
                  <div className="kycMobileCompoundFieldBody">
                    <label className="kycMobileFieldLabel">Annual income</label>
                    <div className="kycMobileCompoundFieldInputs">
                      {renderCurrencyPicker({
                        dropdownKey: "annualIncomeCurrency",
                        value: sow.annualIncomeCurrency || "USD",
                        path: ["sourceOfFundsWealth", "annualIncomeCurrency"],
                        className:
                          "form-select kycMobileCompactSelect kycMobileCompactSelect--currency",
                      })}
                      {renderRangePicker({
                        dropdownKey: "annualIncomeRange",
                        value: sow.annualIncomeRange || "",
                        currency: sow.annualIncomeCurrency || "USD",
                        bucketKeys: ANNUAL_INCOME_BUCKET_KEYS,
                        labels: ANNUAL_INCOME_LABELS,
                        path: ["sourceOfFundsWealth", "annualIncomeRange"],
                        className: `form-select kycMobileCompactSelect ${
                          sow.annualIncomeRange ? "is-selected" : ""
                        } kycMobileCompactSelect--range ${
                          cc(["sourceOfFundsWealth", "annualIncomeRange"])
                            ? "kycReviewChangedChip"
                            : ""
                        }`,
                      })}
                      {sow.annualIncomeCurrency === "AED" && sow.annualIncomeRange ? (
                        <span className="kycSowApproxLabel">
                          ≈ {ANNUAL_INCOME_LABELS.USD[sow.annualIncomeRange]} USD
                        </span>
                      ) : null}
                    </div>
                    {missingAnnualIncome ? (
                      <InlineDangerMessage>Select an annual income range</InlineDangerMessage>
                    ) : null}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ── Card 2: Funding source ── */}
          <div className="kycMobileRepeaterCard" style={{ background: "#F6F4F2", marginTop: 10 }}>
            <div className="kycMobileRepeaterHead">
              <div className="kycMobileSectionIconWrap">
                <Lottie
                  animationData={iconSourceFunds}
                  loop
                  autoplay
                  className="kycMobileSectionLottie"
                />
              </div>
              <div>
                <div className="kycMobileRepeaterTitle">Funding source</div>
                <div className="kycMobileRepeaterHint">
                  Immediate origin of money used in this relationship
                </div>
              </div>
            </div>

            <div className="kycMobileSectionStack">
              <div className="kycMobileFieldCard">
                <label className="kycMobileFieldLabel">{fields.sofLabel}</label>
                <div className="kycMobileFieldHint">Select one or multiple sources</div>
                <div className="kycMobileChipGrid">
                  {availableSofOptions.map((opt) => {
                    const active = sofTypes.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`kycMobileChip ${active ? "is-active" : ""} ${
                          mode === "review" &&
                          active &&
                          changedArrayItem(
                            ["sourceOfFundsWealth", "sourceOfFundsTypes"],
                            opt.value
                          )
                            ? "kycReviewChangedChip"
                            : ""
                        }`}
                        style={active ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                        disabled={readOnly}
                        onClick={() =>
                          onSetAt(
                            ["sourceOfFundsWealth", "sourceOfFundsTypes"],
                            toggleInArray(sofTypes, opt.value)
                          )
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {missingSof ? (
                  <InlineDangerMessage centered>Select at least one source of funds</InlineDangerMessage>
                ) : null}
              </div>

              <AnimatePresence>
                {needsSofLoanExplain ? (
                  <motion.div
                    key="sof-loan"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: 0.22, delay: 0.1 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="kycMobileFieldCard">
                      <label className="kycMobileFieldLabel">Loan details</label>
                      <textarea
                        ref={sofLoanRef}
                        className={`form-control kycMobileAutoGrowTextarea kycSowNarrativeTextarea ${cc([
                          "sourceOfFundsWealth",
                          "sourceOfFundsLoanDetails",
                        ])}`}
                        rows={3}
                        style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                        value={sow.sourceOfFundsLoanDetails || ""}
                        onChange={(e) => {
                          autoResizeTextarea(e.currentTarget);
                          onSetAt(["sourceOfFundsWealth", "sourceOfFundsLoanDetails"], e.target.value);
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        disabled={readOnly}
                        placeholder="Lender name, purpose and repayment terms…"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <AnimatePresence>
                {needsSofOtherExplain ? (
                  <motion.div
                    key="sof-other"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: 0.22, delay: 0.1 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="kycMobileFieldCard">
                      <label className="kycMobileFieldLabel">Other source of funds</label>
                      <textarea
                        ref={sofOtherRef}
                        className={`form-control kycMobileAutoGrowTextarea kycSowNarrativeTextarea ${cc([
                          "sourceOfFundsWealth",
                          "sourceOfFundsOtherDetails",
                        ])}`}
                        rows={3}
                        style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                        value={sow.sourceOfFundsOtherDetails || ""}
                        onChange={(e) => {
                          autoResizeTextarea(e.currentTarget);
                          onSetAt(["sourceOfFundsWealth", "sourceOfFundsOtherDetails"], e.target.value);
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        disabled={readOnly}
                        placeholder="Describe briefly…"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>

          {/* ── Card 3: Payment routes ── */}
          <div className="kycMobileRepeaterCard" style={{ background: "#F6F4F2", marginTop: 10 }}>
            <div className="kycMobileRepeaterHead">
              <div className="kycMobileSectionIconWrap">
                <Lottie
                  animationData={iconPaymentRoutes}
                  loop
                  autoplay
                  className="kycMobileSectionLottie"
                />
              </div>
              <div>
                <div className="kycMobileRepeaterTitle">Payment routes</div>
                <div className="kycMobileRepeaterHint">
                  How the funds are transferred into this relationship
                </div>
              </div>
            </div>

            <div className="kycMobileSectionStack">
              <div className="kycMobileFieldCard">
                <label className="kycMobileFieldLabel">Payment routes</label>
                <div className="kycMobileFieldHint">Select one or multiple routes</div>
                <div className="kycMobileChipGrid">
                  {PAYMENT_ROUTES_OPTIONS.map((opt) => {
                    const active = paymentRoutes.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`kycMobileChip ${active ? "is-active" : ""} ${
                          mode === "review" &&
                          active &&
                          changedArrayItem(
                            ["sourceOfFundsWealth", "paymentRoutes"],
                            opt.value
                          )
                            ? "kycReviewChangedChip"
                            : ""
                        }`}
                        style={active ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                        disabled={readOnly}
                        onClick={() =>
                          onSetAt(
                            ["sourceOfFundsWealth", "paymentRoutes"],
                            toggleInArray(paymentRoutes, opt.value)
                          )
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {missingPaymentRoutes ? (
                  <InlineDangerMessage centered>Select at least one payment route</InlineDangerMessage>
                ) : null}
              </div>

              <AnimatePresence>
                {needsThirdPartyPaymentExplain ? (
                  <motion.div
                    key="payment-routes-third-party"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: 0.22, delay: 0.1 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="kycMobileFieldCard">
                      <label className="kycMobileFieldLabel">Third-party payment details</label>
                      <textarea
                        ref={paymentRoutesThirdPartyRef}
                        className={`form-control kycMobileAutoGrowTextarea kycSowNarrativeTextarea ${cc([
                          "sourceOfFundsWealth",
                          "paymentRoutesThirdPartyDetails",
                        ])}`}
                        rows={3}
                        style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                        value={sow.paymentRoutesThirdPartyDetails || ""}
                        onChange={(e) => {
                          autoResizeTextarea(e.currentTarget);
                          onSetAt(["sourceOfFundsWealth", "paymentRoutesThirdPartyDetails"], e.target.value);
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        disabled={readOnly}
                        placeholder="Name and relationship of the third party…"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <AnimatePresence>
                {needsPaymentRoutesOtherExplain ? (
                  <motion.div
                    key="payment-routes-other"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: 0.22, delay: 0.1 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="kycMobileFieldCard">
                      <label className="kycMobileFieldLabel">Other payment route</label>
                      <textarea
                        ref={paymentRoutesOtherRef}
                        className={`form-control kycMobileAutoGrowTextarea kycSowNarrativeTextarea ${cc([
                          "sourceOfFundsWealth",
                          "paymentRoutesOtherDetails",
                        ])}`}
                        rows={3}
                        style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                        value={sow.paymentRoutesOtherDetails || ""}
                        onChange={(e) => {
                          autoResizeTextarea(e.currentTarget);
                          onSetAt(["sourceOfFundsWealth", "paymentRoutesOtherDetails"], e.target.value);
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        disabled={readOnly}
                        placeholder="Describe briefly…"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
